import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { STOP_TYPE } from '../../enums/operations.enum';
import {
  CreateDeliveryRequest,
  DeliveryPackage,
} from '../../models/operations/operations-delivery.model';
import {
  CreatePickupRequest,
  PickupPackage,
} from '../../models/operations/operations-pickup-stop.model';
import {
  OperationsRouteDetail,
  TableOperationsRouteDetails,
} from '../../models/operations/operations-route-details.model';

@Injectable({
  providedIn: 'root',
})
export class OperationsUtils {
  /**
   * To create details for the table with given route details
   *
   * @param {OperationsRouteDetail[]} pendingStops
   * @return {*}  {TableOperationsRouteDetails[]}
   * @memberof OperationsUtils
   */
  createDetailsTable(
    pendingStops: OperationsRouteDetail[],
  ): TableOperationsRouteDetails[] {
    let parsedAddress: string;
    let streetAddress: string;
    let combinedAddress: string;

    const expandedTable: TableOperationsRouteDetails[] = []; // Filter out 'DS' type with empty or null packagedetails

    // filtering when a Delivery does not have any package
    const filteredStops = pendingStops.filter(
      (stop) =>
        !(
          stop.type === 'DS' &&
          (!stop.packagedetails || stop.packagedetails.length === 0)
        ),
    );

    filteredStops.forEach((data, i) => {
      streetAddress = data.streetparsed?.name ?? '';
      combinedAddress = (data.streetparsed?.number ?? '') + ' ' + streetAddress;
      parsedAddress =
        (data?.streetparsed?.number?.length ?? 0) > 0
          ? combinedAddress
          : streetAddress;

      const infoNotice =
        (data as any).infoNotice ??
        (data as any).deliveryDetail?.infoNotice ??
        (data as any).pickupDetail?.infoNotice ??
        '';

      if (data.packagedetails.length > 0) {
        data.packagedetails.forEach((_data2, k) => {
          expandedTable.push({
            index: i,
            displayStop: k === 0,
            type: data.type,
            id: data.id,
            density: data.density,
            cosignee: data.cosignee,
            address: parsedAddress,
            city: data.city,
            postal: data.postal,
            province: data.province,
            stopDate: new Date(data.stopDate),
            completionTime: data.completiontime,
            trackingNumber: data.packagedetails[k].trackingnumber,
            scanCode: data.packagedetails[k].scancode,
            description: data.packagedetails[k].remark ?? '',
            amtDue:
              data.packagedetails[k].amtdue ??
              data.packagedetails[k].amtDeclared ??
              0,
            amtCollected: data.packagedetails[k].amtcollected,
            eta: data.packagedetails[k].eta,
            commitTime: data.packagedetails[k].committime,
            isStopComplete: !!data.completiontime,
            shipperNumber: data.packagedetails[k].shippernumber,
            position: data.packagedetails[k].position,
            scancodeDesc: data.packagedetails[k].scancodeDesc,
            infoNotice: infoNotice,
          });
        });
      } else {
        expandedTable.push({
          index: i,
          displayStop: true,
          type: data.type,
          id: data.id,
          density: data.density,
          cosignee: data.cosignee,
          address: parsedAddress,
          city: data.city,
          postal: data.postal,
          province: data.province,
          stopDate: new Date(data.stopDate),
          completionTime: data.completiontime,
          trackingNumber: '',
          scanCode: '',
          description: '',
          amtDue: '',
          amtCollected: '',
          eta: '',
          commitTime: '',
          isStopComplete: !!data.completiontime,
          shipperNumber: '',
          prn: data.prn,
          infoNotice: infoNotice,
        });
      }
    });

    expandedTable.sort((a, b) =>
      a.id.toString().localeCompare(b.id.toString()),
    );
    return expandedTable;
  }

  /**
   * To get the route coming from Route Summary page
   * into Route Details/ Pending Stops page
   *
   * @param {OperationsRouteDetail} routeDetail
   * @param {TableOperationsRouteDetails[]} data
   * @return {*}
   * @memberof OperationsUtils
   */
  getSelectedRoute(
    routeDetail: OperationsRouteDetail,
    data: TableOperationsRouteDetails[],
  ) {
    const returnData = data.filter(
      (ele: TableOperationsRouteDetails) => ele?.id === routeDetail?.id,
    );

    // In case no data is found for some reason, return null so we can catch the error and show a banner
    return returnData ?? null;
  }

  editStop(
    value: CreatePickupRequest | CreateDeliveryRequest,
    dataSourceData: TableOperationsRouteDetails[],
  ): TableOperationsRouteDetails[] {
    // Edited row
    const editedRow = dataSourceData.filter((dat) => {
      return dat.id === value.stop?.stopId;
    });

    // Table data other than the edited row
    let remainingTable = dataSourceData.filter((dat) => {
      return dat.id !== value.stop?.stopId;
    });

    // Pickup Type
    if (value.stop?.stopType === STOP_TYPE.PICKUP) {
      (value as CreatePickupRequest).pickupPackages?.forEach(
        (packageDetail, i: number) => {
          remainingTable = this.updatePackageDetails(
            dataSourceData,
            packageDetail,
            editedRow,
            remainingTable,
            i,
          );
        },
      );

      dataSourceData = remainingTable;
      return dataSourceData;
    } else {
      // Delivery Type
      (value as CreateDeliveryRequest).deliveryPackages?.forEach(
        (packageDetail, i: number) => {
          remainingTable = this.updatePackageDetails(
            dataSourceData,
            packageDetail,
            editedRow,
            remainingTable,
            i,
          );
        },
      );

      dataSourceData = remainingTable;
      return dataSourceData;
    }
  }

  updatePackageDetails(
    dataSourceData: TableOperationsRouteDetails[],
    packageDetail: DeliveryPackage | PickupPackage,
    editedRow: TableOperationsRouteDetails[],
    table: TableOperationsRouteDetails[],
    i: number,
  ): TableOperationsRouteDetails[] {
    table.push({
      index: dataSourceData.length,
      displayStop: i === 0,
      type: editedRow[0].type,
      id: editedRow[0].id,
      density: editedRow[0].density,
      cosignee: editedRow[0].cosignee,
      address: editedRow[0].address,
      city: editedRow[0].city,
      postal: editedRow[0].postal,
      province: editedRow[0].province,
      trackingNumber: packageDetail.trackingNumber as string,
      commitTime: editedRow[0].commitTime,
      eta: editedRow[0].eta,
      completionTime: editedRow[0].completionTime,
      scanCode: packageDetail.scanCode as string,
      description: packageDetail.remark as string,
      isStopComplete: !!editedRow[0].completionTime,
      amtDue: packageDetail.paymentDue?.amount as unknown as string,
      amtCollected: packageDetail.payment?.amount as unknown as string,
      shipperNumber: packageDetail.shipperAccount as string,
      stopDate: editedRow[0].stopDate,
      infoNotice: editedRow[0].infoNotice ?? '',
    });

    return table;
  }

  getUniqueValues(value: string, index: number, array: string | string[]) {
    return array.indexOf(value) === index;
  }

  mergeControlErrors(
    control: AbstractControl | null,
    newErrors: ValidationErrors,
  ): void {
    if (!control) return;

    const currentErrors = control.errors || {};
    const mergedErrors = { ...currentErrors, ...newErrors };
    control.setErrors(Object.keys(mergedErrors).length ? mergedErrors : null);
  }
}
